import { useState, useRef, useEffect, type KeyboardEvent } from "react";

type Props = {
  disabled?: boolean;
  onSubmit: (text: string) => void;
  /** Controlled value. When provided, parent owns the textarea state. */
  value?: string;
  /** Controlled onChange. Required when value is provided. */
  onValueChange?: (value: string) => void;
  /** Override placeholder text (e.g., "Press the mic, or write here…"). */
  placeholder?: string;
  /** Focus on mount (default true). Set false when stacked with another input that should own initial focus. */
  autoFocus?: boolean;
};

/**
 * Typed-answer card. Visually a clear container — like an index card or a page
 * torn from a notebook — that invites you to click and write. Cmd/Ctrl+Enter submits.
 *
 * Design intent:
 *   - Reads as a "container you write in," not "a hidden form field."
 *   - Resting state: visible 1px border, soft drop shadow, generous padding.
 *   - Hover: border deepens, shadow lifts slightly (clickable affordance).
 *   - Focus: border darker still, focus ring softens around it, pen icon
 *     animates a small in/out tilt to signal "ready."
 *   - Placeholder: Fraunces italic in ink-300 — echoes how voice answers render
 *     as "you said: ..." italic serif. Visual continuity between modes.
 *   - Typed text: Inter at body-lg for readability over long answers.
 *   - The card grows with content (auto-resize), shrinks back when empty.
 */
export function TypeAnswer({
  disabled,
  onSubmit,
  value,
  onValueChange,
  placeholder = "Write what comes to mind…",
  autoFocus = true,
}: Props) {
  const controlled = value !== undefined;
  const [internalText, setInternalText] = useState("");
  const text = controlled ? (value ?? "") : internalText;
  const setText = (next: string) => {
    if (controlled) onValueChange?.(next);
    else setInternalText(next);
  };
  const [isFocused, setIsFocused] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Auto-resize the textarea to its content, but cap the height so a long answer
  // (or fast live dictation) scrolls inside the card instead of growing the page.
  // Once capped, pin to the bottom so the newest words stay in view.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const MIN = 96;
    const MAX = 220;
    el.style.height = "auto";
    const next = Math.min(MAX, Math.max(MIN, el.scrollHeight));
    el.style.height = `${next}px`;
    if (el.scrollHeight > next) el.scrollTop = el.scrollHeight;
  }, [text]);

  // Auto-focus on mount (opt-in).
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);

  // Clicking anywhere on the card focuses the textarea.
  const focusTextarea = () => {
    ref.current?.focus();
  };

  const handleKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const submit = () => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    onSubmit(trimmed);
    setText("");
  };

  const canSubmit = text.trim().length > 0 && !disabled;

  return (
    <div className="w-full max-w-2xl space-y-4">
      {/* The clickable writing card */}
      <div
        ref={cardRef}
        onClick={focusTextarea}
        className={[
          "group relative rounded-3xl cursor-text",
          "bg-parchment-100",
          "border transition-all duration-300",
          // The open stage only shows this when a reply is needed, so the card
          // carries a soft glow stroke to invite the answer (stronger on focus).
          isFocused
            ? "border-accent-600/45 shadow-[0_0_0_4px_oklch(52%_0.14_30/0.10),0_8px_28px_-12px_oklch(22%_0.02_60/0.18)]"
            : "border-parchment-300 shadow-[0_0_0_3px_oklch(52%_0.14_30/0.06),0_4px_18px_-12px_oklch(22%_0.02_60/0.12)] hover:border-accent-600/30 hover:shadow-[0_0_0_4px_oklch(52%_0.14_30/0.08),0_6px_22px_-12px_oklch(22%_0.02_60/0.15)]",
        ].join(" ")}
      >
        <textarea
          ref={ref}
          aria-label="Type your answer"
          placeholder={placeholder}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onKeyDown={handleKey}
          disabled={disabled}
          rows={3}
          spellCheck
          className={[
            "write-area w-full px-5 sm:px-7 pt-7 pb-6",
            "bg-transparent",
            "text-ink-900 placeholder:text-ink-500/70 placeholder:italic placeholder:font-display",
            "font-sans text-lg leading-[1.65]",
            "resize-none overflow-y-auto",
            "focus:outline-none",
            "rounded-3xl",
          ].join(" ")}
        />

        {/* Soft baseline line at the bottom of the card */}
        <div
          aria-hidden="true"
          className={`absolute bottom-5 left-7 right-7 h-px transition-colors duration-500 ${
            isFocused ? "bg-ink-300/40" : "bg-parchment-300/60"
          }`}
        />
      </div>

      {/* Action row — outside the card so it reads as the "save" affordance */}
      <div className="flex items-center justify-between px-2">
        <p className="text-xs text-ink-300 flex items-center gap-1">
          <kbd className="px-1.5 py-0.5 rounded bg-parchment-200/80 text-ink-500 text-[10px] font-mono">⌘</kbd>
          <span>+</span>
          <kbd className="px-1.5 py-0.5 rounded bg-parchment-200/80 text-ink-500 text-[10px] font-mono">Enter</kbd>
          <span className="ml-1">to send</span>
        </p>
        <button
          type="button"
          disabled={!canSubmit}
          onClick={submit}
          className={[
            "px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300",
            canSubmit
              ? "bg-ink-900 text-parchment-50 hover:bg-ink-700 active:scale-[0.98] shadow-[0_4px_14px_-6px_oklch(22%_0.02_60/0.4)]"
              : "bg-parchment-200/60 text-ink-300 cursor-not-allowed",
          ].join(" ")}
        >
          Send
        </button>
      </div>
    </div>
  );
}
